import Tooltip from 'components/atomic/molecules/tooltip';
import Container from 'components/atomic/atoms/container';
import { InfoIcon } from 'components/atomic/atoms/icons';
import { usePriceInfoTooltip } from './use-price-info-tooltip';
import type { SupportedCurrency } from 'utils/i18n-format-currency';

export interface PriceInfoTooltipProps {
  amount: number | null;
  currency: SupportedCurrency;
  blur?: boolean;
  secondaryText?: string;
}

/**
 * PriceInfoTooltip - Displays price information tooltip
 * 
 * Shows a small "i" icon that displays a tooltip with price context.
 * Uses usePriceInfoTooltip hook internally to derive tooltip text.
 * 
 * Behavior:
 * - Desktop: Tooltip appears on hover
 * - Mobile: Tooltip appears on click/tap (with click-away to dismiss)
 * - Only renders when price has value and is not blurred
 */
export function PriceInfoTooltip({
  amount,
  currency,
  blur,
  secondaryText,
}: PriceInfoTooltipProps) {
  const {
    tooltipText,
    secondaryText: derivedSecondaryText,
    ariaLabel,
    shouldShow,
  } = usePriceInfoTooltip({
    amount,
    currency,
    blur,
    secondaryText,
  });

  if (!shouldShow) {
    return null;
  }

  const tooltipContent = (
    <Container sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
      <span>{tooltipText}</span>
      {derivedSecondaryText && (
        <>
          <br /><br />
          <span>{derivedSecondaryText}</span>
        </>
      )}
    </Container>
  );

  return (
    <Container 
      component="span" 
      sx={{ 
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '8px',
      }}
      data-testid="ui-price-info-tooltip"
    >
      <Tooltip
        content={tooltipContent}
        placement="top"
        ariaLabel={ariaLabel}
      >
        <InfoIcon size={16} />
      </Tooltip>
    </Container>
  );
}

export default PriceInfoTooltip;
